<template>
  <div class="flex size-full gap-5 overflow-hidden mobile:flex-col">
    <div :class="['size-full']">
      <highcharts
        ref="chartRef"
        class="h-full"
        :options="chartOptions"
      />
    </div>
    <div class="h-px w-full bg-white/20" />
    <div class="flex h-[122px] flex-col items-start justify-center gap-2 overflow-y-auto">
      <div
        v-for="(item, index) in walletNameList"
        :key="item.name"
        class="flex cursor-pointer items-center gap-2"
        @click="toggleSeries(index)"
      >
        <div
          :style="{ backgroundColor: item.color, opacity: seriesVisibleList[index] ? 1 : 0.3 }"
          class="size-2 rounded-full"
        />
        <span
          class="font-semilight text-[12px] text-white/40"
          :style="{ textDecoration: seriesVisibleList[index] ? 'none' : 'line-through', opacity: seriesVisibleList[index] ? 1 : 0.4 }"
        >
          {{ item.name }}
        </span>
      </div>
    </div>
    <DatePicker
      :model-value="dateRange"
      :default-value="'180d'"
      class="w-full"
      @update:model-value="changeDateRange"
    />
  </div>
</template>

<script setup lang="ts">
import type { TooltipFormatterContextObject } from 'highcharts'
import { computed, nextTick, ref, watch } from 'vue'

import type { LivewatchWalletBalanceTracker } from '~/api/service/livewatch/wallet-balance-tracker.service'
import DatePicker from '~/components/@pages/livewatch/charts/date-picker/DatePicker.vue'
import { setCommaWithDigits } from '~/utils/numberFormat'

interface Props {
    dataList: LivewatchWalletBalanceTracker['walletList']
}

const props = defineProps<Props>()

const chartRef = ref()
const dateRange = ref<[Date | null, Date | null]>([ null, null ])
const { createCustomTooltip } = useChartTooltip()

// 범례 가시성 상태를 별도로 관리 (Vue reactivity 보장)
const seriesVisibleList = ref<boolean[]>([])

const colorList = [
  '#6ea8fe',
  '#ffc400',
  '#ff9800',
  '#f44336',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
]

// 초기 상태 설정
watch(() => props.dataList, (newDataList) => {
  if (newDataList?.length) {
    seriesVisibleList.value = newDataList.map(() => true)
  }
}, { immediate: true })

const walletNameList = computed(() => {
  return props.dataList.map((item, index) => ({
    name: item.name,
    color: colorList[index % colorList.length]
  }))
})

function toggleSeries(index: number) {
  const chart = chartRef.value?.chart || chartRef.value
  if (!chart) return
  
  const series = chart.series[index]
  if (series) {
    // 차트 상태 변경
    if (series.visible) {
      series.hide()
    } else {
      series.show()
    }
    
    // Vue 상태 동기화
    seriesVisibleList.value[index] = series.visible
  }
}

const getTimestampFromPoint = (point: Highcharts.Point): number => {
  if ('x' in point && typeof point.x === 'number') {
    return point.x
  }
  if (Array.isArray(point) && typeof point[0] === 'number') {
    return point[0]
  }
  return 0
}

// 날짜 범위가 변경될 때 차트의 xAxis 범위를 안전하게 조정한다.
// 데스크톱 버전과 동일한 로직으로 실제 데이터 범위를 벗어나지 않도록 보호한다.
const changeDateRange = (dateRange: [Date | null, Date | null]) => {
  const chart = chartRef.value?.chart || chartRef.value
  if (!chart) return

  const series = chart.series[0]
  if (!series?.data?.length) return

  // 실제 데이터 범위 - 첫 번째 시리즈 기준
  const dataPoints = series.data
  let actualStartDate = getTimestampFromPoint(dataPoints[0])
  let actualEndDate = getTimestampFromPoint(dataPoints[dataPoints.length - 1])

  // 다른 시리즈들의 범위도 확인하여 전체 범위 계산
  if (chart.series.length > 1) {
    for (let i = 1; i < chart.series.length; i++) {
      const otherSeries = chart.series[i]
      if (!otherSeries?.data?.length) continue
      
      const firstTs = getTimestampFromPoint(otherSeries.data[0])
      const lastTs = getTimestampFromPoint(otherSeries.data[otherSeries.data.length - 1])
      
      if (firstTs < actualStartDate) actualStartDate = firstTs
      if (lastTs > actualEndDate) actualEndDate = lastTs
    }
  }

  // 선택된 날짜 범위
  let startDate = dateRange[0] ? new Date(dateRange[0]).setHours(0, 0, 0, 0) : actualStartDate
  let endDate = dateRange[1] ? new Date(dateRange[1]).setHours(23, 59, 59, 999) : actualEndDate

  // 데이터 범위를 벗어나지 않도록 제한
  startDate = Math.max(startDate, actualStartDate)
  endDate = Math.min(endDate, actualEndDate)

  // 선택된 기간이 데이터보다 길면 전체 데이터 범위 사용
  const selectedRange = endDate - startDate
  const dataRange = actualEndDate - actualStartDate
  
  if (selectedRange > dataRange) {
    startDate = actualStartDate
    endDate = actualEndDate
  }

  chart.xAxis[0].setExtremes(startDate, endDate)
}

const chartOptions = computed(() => {
  return {
    title: null,
    scrollbar: { enabled: false },
    chart: {
      height: 300,
      width: null,
      backgroundColor: 'transparent',
      zooming: {
        mouseWheel: {
          enabled: false
        }
      },
      events: {
        load: function () {
          // 차트 로드 후 초기 상태 동기화
          nextTick(() => {
            const chart = chartRef.value?.chart || chartRef.value
            if (chart) {
              seriesVisibleList.value = chart.series.map((series: Highcharts.Series) => series.visible)
            }
          })
        }
      }
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        millisecond: '%H:%M:%S.%L',
        second: '%H:%M:%S',
        minute: '%H:%M', 
        hour: '%H:%M',
        day: '%m/%d',
        week: '%m/%d',
        month: '%Y-%m',
        year: '%Y'
      },
      labels: {
        style: {
          color: '#FFFFFF'
        }
      },
      lineColor: '#2C3241',
      tickColor: '#2C3241',
      gridLineColor: '#2C3241',
      crosshair: {
        color: '#FFFFFF'
      }
    },
    yAxis: {
      visible: true,
      title: {
        text: null
      },
      opposite: true,
      labels: {
        style: {
          color: '#FFFFFF'
        }
      },
      gridLineColor: '#B0B8D033'
    },
    legend: {
      enabled: false
    },
    series: props.dataList.map((item, index) => ({
      type: 'line',
      name: `${item.name.slice(0, 6)}...${item.name.slice(-4)}`,
      data: item.data,
      color: colorList[index % colorList.length],
      marker: {
        enabled: item.data.length === 1,
        states: {
          hover: {
            enabled: true
          }
        }
      }
    })),
    tooltip: {
      split: false,
      shared: true,
      shadow: false,
      outside: true,
      useHTML: true,
      padding: 8,
      backgroundColor: 'none',
      borderWidth: 0,
      borderRadius: 0,
      style: {
        fontSize: '12px',
        fontFamily: 'Montserrat',
      },
      formatter: function (this: TooltipFormatterContextObject) {
        return createCustomTooltip(this, {
          containerClass: 'w-[300px]',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          date: (timestamp: any) => `<p class="font-bold">${new Date(timestamp).toISOString().slice(0, 10)}</p>`,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          content: (points: any[]) => {
            return points.map(point => 
              `<div class="flex w-full items-center justify-between">
                <p><span style="color:${point.series.userOptions.color || point.color};">● </span>${point.series.name}</p>
                <div class="font-bold">${setCommaWithDigits(point.y, 2)} NXPC</div>
              </div>`
            ).join('')
          }
        })
      }
    },
  }
})

function updateChartSize() {
  nextTick(() => {
    const chart = chartRef.value?.chart || chartRef.value
    if (chart && typeof chart.reflow === 'function') {
      chart.reflow()
            
    }
  })
}

// 화면 크기 변경에도 대응
window.addEventListener('resize', updateChartSize)
</script>